import { auth, authActions } from "@/services/auth";
import { StateCreator } from "zustand";
import { AuthState, AuthUser } from "@/types/v1";
import { AuthCustomClaims } from "@/types/v2";
import { UserSlice } from "@/stores/user.slice";
import { notificationService } from "@/services/notifications";

export interface AuthSlice {
  initialized: boolean;
  setInitialized: (initialized: boolean) => void;
  authUser: AuthUser | undefined;
  setAuthUser: (user: AuthUser | undefined) => void;
  authState: AuthState;
  setAuthState: (state: AuthState | undefined) => void;
  authError: string | undefined;
  setAuthError: (error: string | undefined) => void;
  authListener: () => void;
  authClaims: AuthCustomClaims | undefined;
  setAuthClaims: (claims: AuthCustomClaims | undefined) => void;
}

const initialSlice: Pick<
  AuthSlice,
  "initialized" | "authState" | "authError" | "authUser" | "authClaims"
> = {
  initialized: false,
  authUser: undefined,
  authState: "unauthenticated",
  authError: undefined,
  authClaims: undefined,
};

export const createAuthSlice: StateCreator<
  AuthSlice & UserSlice,
  [],
  [],
  AuthSlice
> = (set, get) => ({
  ...initialSlice,
  setInitialized: (initialized) => set({ initialized }),
  setAuthUser: (user) => set({ authUser: user }),
  setAuthState: (state) => set({ authState: state }),
  setAuthError: (error) => set({ authError: error }),
  setAuthClaims: (claims) => set({ authClaims: claims }),
  authListener: () => {
    auth.onAuthStateChanged((user) => {
      const currentAuth = get().authUser;
      if (user && user.uid !== currentAuth?.uid) {
        authActions.getUserClaims().then((claims) =>
          set({
            authClaims: {
              user: claims?.user ?? false,
              superAdmin: claims?.superAdmin ?? false,
              shops: claims?.shops ?? {},
            },
          })
        );
        get()
          .subscribeUser(user)
          .then(() => {
            set({
              authState: user.emailVerified ? "authenticated" : "unverified",
              authUser: {
                uid: user.uid,
                email: user.email,
                emailVerified: user.emailVerified,
              },
              initialized: true,
            });

            // Request notification permission after successful authentication
            if (user.emailVerified) {
              notificationService.requestPermission().catch((error) => {
                console.warn(
                  "Failed to request notification permission:",
                  error
                );
              });
            }
          });
      } else {
        get().resetUser();
        set({
          ...initialSlice,
          initialized: true,
          authClaims: undefined,
        });
      }
    });
  },
});
